import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims, claimsEvaluations } from './claims';
import { contacts } from './contacts';
import { backgroundJobStatusEnum, backgroundJobTypeEnum, communicationTypeEnum, processedEmailResultEnum, processedEmailReviewStatusEnum } from './enums';
import { leads } from './leads';
import { organizations } from './organizations';
import { users } from './users';

// Communications table
export const communications = pgTable("communications", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id").references(() => contacts.id),
  userId: varchar("user_id").references(() => users.id),
  type: communicationTypeEnum("type").notNull(),
  direction: varchar("direction").notNull(), // inbound, outbound
  subject: varchar("subject"),
  content: text("content"),
  
  // Sender information for inbound communications (especially emails)
  senderEmail: varchar("sender_email"),
  senderName: varchar("sender_name"),
  senderPhone: varchar("sender_phone"),
  gmailMessageId: text("gmail_message_id"),
  gmailThreadId: text("gmail_thread_id"),

  scheduledAt: timestamp("scheduled_at"),
  completedAt: timestamp("completed_at"),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("uq_communications_gmail_claim").on(table.gmailMessageId, table.claimId),
]);


// Email drafts table for saving emails before sending
export const emailDrafts = pgTable("email_drafts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id").references(() => contacts.id),
  to: varchar("to").notNull(),
  cc: varchar("cc"),
  bcc: varchar("bcc"),
  subject: varchar("subject").notNull(),
  body: text("body").notNull(),
  status: varchar("status").default("draft").notNull(), // draft, reviewed, sent, cancelled, failed
  priority: varchar("priority").default("normal"), // low, normal, high, urgent
  scheduledFor: timestamp("scheduled_for"),
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  sentBy: varchar("sent_by").references(() => users.id),
  sentAt: timestamp("sent_at"),
  communicationId: uuid("communication_id").references(() => communications.id, { onDelete: "cascade" }), // Link to actual sent communication
  createdBy: varchar("created_by").references(() => users.id),
  
  // AI Automation fields
  gmailDraftId: varchar("gmail_draft_id"), // Gmail API draft ID
  gmailThreadId: varchar("gmail_thread_id"), // Gmail thread ID for tracking conversation
  emailFrom: varchar("email_from"), // Original sender email we're responding to
  tone: varchar("tone"), // professional, friendly, firm, legal
  aiContext: jsonb("ai_context"), // Research context used: claim data, statutes, similar outcomes
  processingStatus: varchar("processing_status").default("pending"), // pending, processing, completed, failed
  error: text("error"), // Error message if processing failed
  attachmentMetadata: jsonb("attachment_metadata"), // Info about processed attachments
  draftType: varchar("draft_type").default('user_draft'), // 'user_draft' | 'status_update'

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_email_drafts_claim_id").on(table.claimId),
  index("idx_email_drafts_status").on(table.status),
  index("idx_email_drafts_created_by").on(table.createdBy),
  index("idx_email_drafts_processing_status").on(table.processingStatus),
  index("idx_email_drafts_gmail_thread_id").on(table.gmailThreadId),
]);


// Email accounts table for Google email integration
export const emailAccounts = pgTable("email_accounts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  // The owning firm. NULLABLE (MIS-86): added by the MANUAL migration
  // drizzle/manual/0108_email-accounts-org-rls.sql, which also enables per-firm RLS.
  // A row with a NULL org_id is invisible to role app_user under RLS until an owner
  // assigns it — the backfill only stamps mailboxes whose firm is safely derivable
  // (a personal mailbox whose owner belongs to exactly one firm); shared or
  // multi-firm mailboxes are left NULL deliberately. See resolveMailboxOrgId.
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id), // Nullable for shared accounts
  email: varchar("email").notNull(),
  refreshToken: text("refresh_token").notNull(),
  accessToken: text("access_token"),
  expiryDate: timestamp("expiry_date"),
  isActive: boolean("is_active").default(true),
  status: varchar("status", { length: 20 }).default('active'), // active, token_expired, revoked
  lastError: text("last_error"), // Store last error message for debugging
  isShared: boolean("is_shared").default(false), // True for shared company email accounts
  accountLabel: varchar("account_label"), // Friendly name (e.g., "Claims Intake", "Company Email")
  allowedRoles: text("allowed_roles").array(), // Array of role names that can access this shared email
  lastHistoryId: text("last_history_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_email_accounts_user_id").on(table.userId),
  index("idx_email_accounts_email").on(table.email),
  index("idx_email_accounts_status").on(table.status),
  index("idx_email_accounts_is_shared").on(table.isShared),
  index("idx_email_accounts_org_id").on(table.orgId),
]);


// Automated communications log for tracking policyholder education
export const automatedCommunications = pgTable("automated_communications", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  evaluationId: uuid("evaluation_id").references(() => claimsEvaluations.id, { onDelete: "cascade" }),
  
  communicationType: varchar("communication_type").notNull(), // email, call, sms
  templateUsed: varchar("template_used"),
  recipient: varchar("recipient").notNull(), // Email or phone
  subject: varchar("subject"),
  content: text("content"),
  
  status: varchar("status").default("pending"), // pending, sent, delivered, failed
  scheduledAt: timestamp("scheduled_at"),
  sentAt: timestamp("sent_at"),
  deliveredAt: timestamp("delivered_at"),
  
  isEducational: boolean("is_educational").default(true),
  educationTopic: varchar("education_topic"), // deductible, exclusions, process, timeline
  
  metadata: jsonb("metadata"), // Provider-specific data (message ID, call duration, etc.)
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_automated_communications_claim_id").on(table.claimId),
  index("idx_automated_communications_status").on(table.status),
  index("idx_automated_communications_type").on(table.communicationType),
]);


// Email Templates for drafting emails
export const emailTemplates = pgTable("email_templates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Template info
  templateName: varchar("template_name").notNull(),
  templateType: varchar("template_type").notNull(), // 'reminder', 'depreciation_release', 'completion_request'
  subject: text("subject").notNull(),
  body: text("body").notNull(),
  
  // Variables that can be replaced
  availableVariables: text("available_variables"), // JSON array of available variables
  
  isActive: boolean("is_active").default(true),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_email_templates_type").on(table.templateType),
  index("idx_email_templates_is_active").on(table.isActive),
]);


// Email Drafts and History
export const emailCommunications = pgTable("email_communications", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  templateId: uuid("template_id").references(() => emailTemplates.id),
  
  // Email details
  recipientType: varchar("recipient_type").notNull(), // 'policyholder', 'insurance_company', 'contractor'
  recipientEmail: varchar("recipient_email").notNull(),
  recipientName: varchar("recipient_name"),
  ccEmails: text("cc_emails"), // JSON array of CC emails
  
  subject: text("subject").notNull(),
  body: text("body").notNull(),
  
  // Attachments
  attachments: text("attachments"), // JSON array of attachment IDs and names
  
  // Status
  status: varchar("status").default("draft"), // 'draft', 'scheduled', 'sent', 'failed'
  scheduledFor: timestamp("scheduled_for"),
  sentAt: timestamp("sent_at"),
  
  // Tracking
  deliveryStatus: varchar("delivery_status"),
  openedAt: timestamp("opened_at"),
  clickedLinks: text("clicked_links"), // JSON array of clicked links
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_email_comms_claim_id").on(table.claimId),
  index("idx_email_comms_status").on(table.status),
  index("idx_email_comms_scheduled_for").on(table.scheduledFor),
  index("idx_email_comms_recipient_type").on(table.recipientType),
]);


// AI Feature #7: Communication Thread Summaries
export const communicationSummaries = pgTable("communication_summaries", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Summary content
  summary: text("summary").notNull(),
  keyPoints: jsonb("key_points"), // Array of key discussion points
  actionItems: jsonb("action_items"), // Array of identified action items
  decisions: jsonb("decisions"), // Array of decisions made
  
  // Thread details
  communicationIds: jsonb("communication_ids"), // Array of communication IDs in this thread
  startDate: timestamp("start_date"),
  endDate: timestamp("end_date"),
  participantCount: integer("participant_count"),
  messageCount: integer("message_count"),
  
  // Analysis
  sentiment: varchar("sentiment"), // positive, neutral, negative, mixed
  urgency: varchar("urgency"), // low, medium, high, critical
  topics: jsonb("topics"), // Array of main topics discussed
  
  // Metadata
  modelUsed: varchar("model_used"),
  generatedBy: varchar("generated_by").references(() => users.id),
  generatedAt: timestamp("generated_at").defaultNow(),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_communication_summaries_claim_id").on(table.claimId),
  index("idx_communication_summaries_urgency").on(table.urgency),
]);


export const processedEmails = pgTable("processed_emails", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Gmail identifiers — gmailMessageId is the IDEMPOTENCY KEY
  gmailMessageId: text("gmail_message_id").notNull().unique(),
  gmailThreadId: text("gmail_thread_id"),

  // Email metadata
  emailFrom: text("email_from").notNull(),
  emailTo: text("email_to"),
  emailSubject: text("email_subject"),
  emailBodySnippet: text("email_body_snippet"), // First 500 chars for audit trail display
  emailDate: timestamp("email_date"), // Original email date

  // Processing info
  syncSessionId: text("sync_session_id"), // Groups emails from one button click
  processedBy: varchar("processed_by").references(() => users.id),
  emailAccountId: uuid("email_account_id"),

  // AI classification result
  result: processedEmailResultEnum("result").notNull(),
  confidenceScore: integer("confidence_score").default(0), // 0-100
  aiReasoning: text("ai_reasoning"), // AI's explanation

  // Linked entities (all nullable — depends on result)
  matchedClaimId: uuid("matched_claim_id").references(() => claims.id, { onDelete: "set null" }),
  createdLeadId: uuid("created_lead_id").references(() => leads.id, { onDelete: "set null" }),

  // Processing details (JSONB for flexibility)
  contactsAdded: jsonb("contacts_added").$type<Array<{ contactId: string; name: string; email: string; isNew: boolean }>>(),
  attachmentsFiled: jsonb("attachments_filed").$type<Array<{ documentId: string; filename: string; folder: string }>>(),
  triggersExecuted: jsonb("triggers_executed").$type<Array<{ type: string; draftId?: string; gmailDraftId?: string }>>(),

  // Human-in-the-loop review
  reviewStatus: processedEmailReviewStatusEnum("review_status").default("not_needed"),
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),

  // Error tracking
  errorMessage: text("error_message"),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  processedAt: timestamp("processed_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_processed_emails_gmail_msg").on(table.gmailMessageId),
  index("idx_processed_emails_session").on(table.syncSessionId),
  index("idx_processed_emails_result").on(table.result),
  index("idx_processed_emails_review").on(table.reviewStatus),
  index("idx_processed_emails_claim").on(table.matchedClaimId),
  index("idx_processed_emails_lead").on(table.createdLeadId),
  index("idx_processed_emails_processed_at").on(table.processedAt),
  index("idx_processed_emails_account").on(table.emailAccountId),
  index("idx_processed_emails_gmail_thread_id").on(table.gmailThreadId),
]);

// Email address history for autocomplete suggestions
export const emailAddressHistory = pgTable("email_address_history", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  email: varchar("email").notNull(),
  name: varchar("name"),
  usageCount: integer("usage_count").default(1).notNull(),
  lastUsedAt: timestamp("last_used_at").defaultNow().notNull(),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  uniqueIndex("uq_email_address_history_user_email").on(table.userId, table.email),
  index("idx_email_address_history_user_id").on(table.userId),
  index("idx_email_address_history_last_used").on(table.lastUsedAt),
]);


// Generic background job tracking — one row per running/completed job, scoped by user and job type
export const backgroundJobs = pgTable("background_jobs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  jobType: backgroundJobTypeEnum("job_type").notNull(),
  jobRef: varchar("job_ref").notNull().unique(), // business ID (e.g. syncSessionId)
  userId: varchar("user_id").references(() => users.id), // null for system jobs
  status: backgroundJobStatusEnum("status").notNull().default("running"),
  payload: jsonb("payload"), // job-type-specific data; email_sync: { emailAccountId, totalEmails }
  error: text("error"),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
}, (table) => [
  index("idx_background_jobs_user_id").on(table.userId),
  index("idx_background_jobs_job_type").on(table.jobType),
  index("idx_background_jobs_status").on(table.status),
  index("idx_background_jobs_job_ref").on(table.jobRef),
]);
